/**
 * core/shadow-utils.js
 * Quản lý chính sách đổ bóng Three.js (Shadow Policy) cho Voxel Anim Studio.
 * Cung cấp helper bật castShadow chọn lọc dựa trên kích thước hình học (boundingSphere radius),
 * loại trừ các mesh chi tiết nhỏ (ốc vít, nan hoa, tán lá nhỏ, bóng giả BlobShadow...)
 * nhằm tối ưu hiệu năng Shadow Map pass.
 */

/**
 * Bật castShadow một cách chọn lọc theo bán kính boundingSphere.
 * @param {import('three').Object3D} root - Cây đối tượng 3D cần duyệt
 * @param {object} options
 * @param {number} [options.minRadius=0.15] - Ngưỡng bán kính tối thiểu để bật castShadow
 */
export function enableShadowCasting(root, { minRadius = 0.15 } = {}) {
  if (!root) return;

  root.traverse((m) => {
    if (!m.isMesh) return;

    // BlobShadow luôn có castShadow = false
    if (m.name === 'BlobShadow') {
      m.castShadow = false;
      return;
    }

    if (!m.geometry) {
      m.castShadow = false;
      return;
    }

    if (!m.geometry.boundingSphere) {
      m.geometry.computeBoundingSphere();
    }

    const sphere = m.geometry.boundingSphere;
    if (!sphere) {
      m.castShadow = false;
      return;
    }

    // Tính bán kính đã nhân scale lớn nhất của mesh
    const maxScale = Math.max(Math.abs(m.scale.x || 1), Math.abs(m.scale.y || 1), Math.abs(m.scale.z || 1));
    const radius = sphere.radius * maxScale;

    m.castShadow = radius >= minRadius;
  });
}

/**
 * Bật castShadow tối ưu riêng cho phương tiện giao thông (ô tô VinFast, xe máy Wave).
 * Chỉ thân xe và bánh xe đổ bóng, loại bỏ hàng trăm nan hoa, đũa xe, nẹp chrome, đèn nhỏ.
 * @param {import('three').Object3D} vehicle - Đối tượng xe
 * @param {object} options
 * @param {boolean} [options.isMotorbike=false] - True nếu là xe máy
 */
export function enableVehicleShadowCasting(vehicle, { isMotorbike = false } = {}) {
  if (!vehicle) return;

  // Xe máy: bánh xe wr=0.3, thân chính >=0.22; nan hoa/gương/ốc vít <0.20
  // Ô tô: bánh xe wr=0.36, thân xe/cabin >=0.35; chi tiết nhỏ <0.30
  const minRadius = isMotorbike ? 0.22 : 0.35;
  enableShadowCasting(vehicle, { minRadius });
}
